import { CSSProperties } from "react";
import useWebsiteBuilder, { PresavePlatform, PresaveWebsiteBuilderStyle } from "Hooks/useWebsiteBuilder";
import WebsiteBuilderHeader from "../WebsiteBuilderHeader";
import CardSymphony from "elements/CardSymphony";
import InputSymphony from "elements/InputSymphony";
import SwitchSymphony from "elements/SwitchSymphony";
import Sty from "modules/Sty";
import Divider from "elements/Divider";
import ButtonSymphony from "elements/ButtonSymphony";
import DatePickerSymphony from "elements/DatePickerSymphony";
import dayjs from "dayjs";
import TimezoneSelect from "components/shareable/TimezoneSelect";
import Banner from "elements/Banner";
import Icon from "elements/Icon";
import Colors from "modules/Colors";
import Intercom from "helpers/Intercom";
import { clsx, makeKey, styx } from "modules/Utils";
import { WebsiteBuilder_presaveExists } from "Hooks/useWebsiteBuilder";
import OptionsSymphony, { OptionItem } from "elements/OptionsSymphony";
import WebsiteBuilderFooter from "../WebsiteBuilderFooter";

const PRESAVE_STYLE_OPTIONS: OptionItem[] = [
    {
        id: PresaveWebsiteBuilderStyle.BOXES,
        label: 'Boxes',
    },
    {
        id: PresaveWebsiteBuilderStyle.STACKED,
        label: 'Stacked',
    },
    {
        id: PresaveWebsiteBuilderStyle.TRADITIONAL,
        label: 'Traditional',
    },
];

interface PresaveContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const PresaveContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: PresaveContentProps) => {
    const {
        websiteContent,
        setWebsiteContent,
    } = useWebsiteBuilder() as WebsiteBuilder_presaveExists;

    const presaveContent = websiteContent[contentIndex].presaveContent;

    const getPresavePlatformInfo = (platform: PresavePlatform): {
        title: string;
        subtitle: string;
        icon: string;
        isStatic?: boolean;
        onClick?: () => void;
    } => {
        switch (platform) {
            case PresavePlatform.SPOTIFY:
                return {
                    title: 'Pre-save on Spotify',
                    subtitle: '⚡ Fans who presave will auto-follow your profile.',
                    icon: 'spotify',
                    isStatic: true,
                };
            case PresavePlatform.APPLE_MUSIC:
                return {
                    title: 'Pre-add on Apple Music',
                    subtitle: 'This platform doesn\'t enable auto-following.',
                    icon: 'appleMusic',
                };
            case PresavePlatform.AUDIOMACK:
                return {
                    title: 'Pre-save on Audiomack',
                    subtitle: 'Connect Audiomack to enable autofollows.',
                    onClick: () => { },
                    icon: 'audiomack',
                };
            case PresavePlatform.SOUNDCLOUD:
                return {
                    title: 'Pre-save on SoundCloud',
                    subtitle: 'Connect SoundCloud to enable autofollows.',
                    onClick: () => { },
                    icon: 'soundcloud',
                };
            case PresavePlatform.DEEZER:
                return {
                    title: 'Pre-save on Deezer',
                    subtitle: 'Connect Deezer to enable autofollows.',
                    onClick: () => { },
                    icon: 'deezer',
                };
            default:
                return {
                    title: platform,
                    subtitle: 'Connect your account to enable autofollows.',
                    icon: 'questionMark',
                };
        }
    }

    const renderPresaveOptions = () => {
        return (
            <CardSymphony
                className="presavePreviewContentCard"
                title="Options"
                size="small"
                isNoPadding
            >
                <SwitchSymphony
                    className="presaveRedirectToCustomWebpageSwitch"
                    checked={presaveContent.presave.redirect_to_custom_webpage}
                    onChange={() => {
                        const updatedContent = [...websiteContent];
                        updatedContent[contentIndex].presaveContent.presave.redirect_to_custom_webpage = !presaveContent.presave.redirect_to_custom_webpage;
                        setWebsiteContent(updatedContent);
                    }}
                    label="After pre-saving send fans to a custom webpage."
                    labelPlacement="right"
                />
                {presaveContent.presave.redirect_to_custom_webpage &&
                    <InputSymphony
                        className="presaveRedirectUrlInput"
                        value={presaveContent.presave.redirect_url || ''}
                        onChange={value => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.redirect_url = value;
                            setWebsiteContent(updatedContent);
                        }}
                        label="Custom webpage URL"
                    />
                }
            </CardSymphony>
        )
    }

    const renderReleaseDetails = () => {
        return (
            <CardSymphony
                className="presaveReleaseDetailsCard"
                title="Release Details"
                size="small"
                isNoPadding
                style={{ marginTop: 24 }}
            >
                <CardSymphony
                    className="presaveReleaseDetailsCard"
                    title="⚡ Boosts"
                    subtitle="Enable boosts to make it even easier to grow."
                    size="small"
                    isNoPadding
                />
                <Divider />
                <div className="presaveReleaseDetailsCardRow" style={sty.presaveReleaseDetailsCardRow}>
                    <CardSymphony
                        className="presaveReleaseDetailsCard"
                        title="Collect Fan Data"
                        subtitle="When enabled, fans who pre-save will be asked to subscribe and consent to sharing their fan data, including name, email, and location."
                        size="small"
                        isNoPadding
                    />
                    <SwitchSymphony
                        className="presaveCollectEmailsSwitch"
                        checked={presaveContent.presave.boosts?.collect_emails}
                        onChange={() => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.boosts.collect_emails = !presaveContent.presave.boosts.collect_emails;
                            setWebsiteContent(updatedContent);
                        }}
                    />
                </div>
                <Divider />
                <div className="presaveReleaseDetailsCardRow" style={sty.presaveReleaseDetailsCardRow}>
                    <CardSymphony
                        className="presaveReleaseDetailsCard"
                        title="Boost Followers"
                        subtitle="When enabled, fans who pre-save will automatically follow your streaming service profile."
                        size="small"
                        isNoPadding
                    />
                    <SwitchSymphony
                        className="presaveBoostFollowsSwitch"
                        checked={presaveContent.presave.boosts.boost_follows}
                        onChange={() => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.boosts.boost_follows = !presaveContent.presave.boosts.boost_follows;
                            setWebsiteContent(updatedContent);
                        }}
                    />
                </div>
                <Divider />
                <div className="presaveReleaseDetailsCardRow" style={sty.presaveReleaseDetailsCardRow}>
                    <CardSymphony
                        className="presaveReleaseDetailsCard"
                        title="Forever Saves"
                        subtitle="Grow saves with every release by asking fans if they want to automatically save your future releases."
                        size="small"
                        isNoPadding
                    />
                    <SwitchSymphony
                        className="presaveAutoPresavesSwitch"
                        checked={presaveContent.presave.boosts.auto_presaves}
                        onChange={() => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.boosts.auto_presaves = !presaveContent.presave.boosts.auto_presaves;
                            setWebsiteContent(updatedContent);
                        }}
                    />
                </div>
                <Divider />
            </CardSymphony>
        )
    }

    const renderUpcIsrcInput = () => {
        return (
            <CardSymphony
                className="presaveUpcIsrcInputCard"
                title="UPC/ISRC"
                subtitle="Enter your release's UPC/ISRC."
                size="small"
                isNoPadding
            >
                <ButtonSymphony
                    className="presaveUpcIsrcInputButton"
                    onClick={() => {
                        window.open('https://www.upcitemdb.com/', '_blank');
                    }}
                    variant="text"
                >
                    Need help finding this info?
                </ButtonSymphony>
                <InputSymphony
                    className="presaveUpcIsrcInput"
                    value={presaveContent.presave.release_url_upc_isrc || ''}
                    onChange={value => {
                        const updatedContent = [...websiteContent];
                        updatedContent[contentIndex].presaveContent.presave.release_url_upc_isrc = value;
                        setWebsiteContent(updatedContent);
                    }}
                    label="UPC/ISRC URL"
                />
                <div className="presaveUpcIsrcInputSubtitle" style={{ ...Sty.text14grey, fontStyle: 'italic' }}>
                    * You can always add this info after setting up your campaign.
                </div>
            </CardSymphony>
        )
    }

    const renderReleaseDateInfo = () => {
        return (
            <CardSymphony
                className="presaveReleaseDateInfoCard"
                title="Release Date"
                subtitle="Enter the release date for your song / album - this is when the song will be added to your fans' music libraries."
                size="small"
                style={{ marginTop: 24 }}
                isNoPadding
            >
                <div className="presaveReleaseDateInfoRow" style={sty.presaveReleaseDateInfoRow}>
                    <DatePickerSymphony
                        className="presaveReleaseDatePicker"
                        selected={new Date(presaveContent.presave.release_date)}
                        onChange={value => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.release_date = dayjs(value).format('YYYY-MM-DD');
                            setWebsiteContent(updatedContent);
                        }}
                    />
                    <TimezoneSelect
                        labelStyle="abbrevNoPrefix"
                        timezone={presaveContent.presave.timezone || 'America/New_York'}
                        onTimezoneSelect={(timezone) => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.timezone = timezone.value;
                            setWebsiteContent(updatedContent);
                        }}
                    />
                </div>
                <SwitchSymphony
                    className="presaveReleaseDateInfoHideReleaseDateSwitch"
                    checked={presaveContent!.hideReleaseDate}
                    onChange={() => {
                        const updatedContent = [...websiteContent];
                        updatedContent[contentIndex].presaveContent.hideReleaseDate = !presaveContent.hideReleaseDate;
                        setWebsiteContent(updatedContent);
                    }}
                    label="Hide release date from your page"
                    labelPlacement="right"
                />
                <Banner
                    className="presaveReleaseDateInfoBanner"
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 16,
                    }}
                >
                    <Icon
                        className="presaveReleaseDateInfoBannerIcon"
                        icon="mapPin"
                        isCircle
                        size={48}
                        style={{ backgroundColor: Colors.purpleLighter }}
                    />
                    <div className="presaveReleaseDateInfoBannerContent">
                        <div className="presaveReleaseDateInfoBannerTitle" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <div className="presaveReleaseDateInfoBannerTitleText">Realtime Release</div>
                            <Icon
                                className="presaveReleaseDateInfoBannerIcon"
                                style={{ backgroundColor: Colors.grey, borderRadius: 100, padding: 2 }}
                                onClick={Intercom.openRealTimeReleaseArticle}
                                icon="questionMark"
                                color="white"
                                size={18}
                            />
                        </div>
                        <div className="presaveReleaseDateInfoBannerText" style={Sty.text16grey}>
                            Automatically convert your pre-save into a "Listen Now" music site for fans where your song / album has already released.
                        </div>
                    </div>
                    <SwitchSymphony
                        className="presaveReleaseDateInfoBannerSwitch"
                        checked={presaveContent.presave.is_real_time_release}
                        onChange={() => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.presave.is_real_time_release = !presaveContent.presave.is_real_time_release;
                            setWebsiteContent(updatedContent);
                        }}
                    />
                </Banner>
            </CardSymphony>
        )
    }

    const renderPresaveMusicPlatforms = () => {
        return (
            <CardSymphony
                className="presaveMusicPlatformsCard"
                title="Music Platforms"
                subtitle="Select the platforms you want fans to be able to save on."
                size="small"
                style={{ marginTop: 24 }}
                isNoPadding
            >
                <div className="presaveMusicPlatformsContent" style={sty.presaveMusicPlatformsContent}>
                    {Object.values(PresavePlatform).map((platform, index) => {
                        const {
                            title,
                            subtitle,
                            icon,
                            isStatic,
                            onClick
                        } = getPresavePlatformInfo(platform);

                        if (!presaveContent.presave.platforms[index]) {
                            return null;
                        }

                        return (
                            <div
                                key={makeKey(title, index, contentIndex)}
                                className="presaveMusicPlatformRow"
                                style={sty.presaveMusicPlatformRow}
                            >
                                <Icon
                                    className="presaveMusicPlatformIcon"
                                    icon={icon}
                                    size={40}
                                />
                                <div className="presaveMusicPlatformLabel">
                                    <div className="presaveMusicPlatformLabelTitle">{title}</div>
                                    <div
                                        className="presaveMusicPlatformSubtitle"
                                        onClick={onClick}
                                        style={styx(
                                            Sty.text14grey,
                                            onClick && {
                                                cursor: 'pointer',
                                                color: Colors.purple,
                                            }
                                        )}
                                    >
                                        {subtitle}
                                    </div>
                                </div>
                                <Icon
                                    style={{ marginLeft: 'auto' }}
                                    className="presavePlatformUpIcon"
                                    icon="chevronUp"
                                    size={24}
                                    color={index === 0 ? Colors.grey : Colors.purple}
                                    onClick={() => {
                                        if (index === 0) {
                                            return
                                        }

                                        const updatedContent = [...websiteContent];
                                        const platforms = [...presaveContent.presave.platforms];
                                        const temp = platforms[index];
                                        platforms[index] = platforms[index - 1];
                                        platforms[index - 1] = temp;
                                        updatedContent[contentIndex].presaveContent.presave.platforms = platforms;
                                        setWebsiteContent(updatedContent);
                                    }}
                                />
                                <Icon
                                    className="presavePlatformDownIcon"
                                    icon="chevronDown"
                                    size={24}
                                    color={index === (presaveContent.presave.platforms.length || 0) - 1 ? Colors.grey : Colors.purple}
                                    onClick={() => {
                                        if (index === (presaveContent.presave.platforms.length || 0) - 1) {
                                            return
                                        }

                                        const updatedContent = [...websiteContent];
                                        const platforms = [...presaveContent.presave.platforms];
                                        const temp = platforms[index];
                                        platforms[index] = platforms[index + 1];
                                        platforms[index + 1] = temp;
                                        updatedContent[contentIndex].presaveContent.presave.platforms = platforms;
                                        setWebsiteContent(updatedContent);
                                    }}
                                />
                                {!isStatic ?
                                    <SwitchSymphony
                                        className="presaveMusicPlatformSwitch"
                                        checked={presaveContent.presave.platforms[index].isEnabled}
                                        onChange={() => {
                                            const updatedContent = [...websiteContent];
                                            const platforms = [...presaveContent.presave.platforms];
                                            platforms[index].isEnabled = !platforms[index].isEnabled;
                                            updatedContent[contentIndex].presaveContent.presave.platforms = platforms;
                                            setWebsiteContent(updatedContent);
                                        }}
                                    />
                                    :
                                    <div style={{ width: 36, height: 20 }} />
                                }
                            </div>
                        )
                    })}
                </div>
            </CardSymphony>
        )
    }

    // NOTE: render()
    return (
        <CardSymphony
            className={clsx("presaveContentComponent", className)}
            style={sty.presaveContentComponent}
            size="small"
            id={id}
        >
            <WebsiteBuilderHeader
                contentIndex={contentIndex}
                rightSideContent={
                    <>
                        {tabSelectorComponent}
                    </>
                }
            />
            {renderPresaveOptions()}
            {renderReleaseDetails()}
            {renderUpcIsrcInput()}
            {renderReleaseDateInfo()}
            {renderPresaveMusicPlatforms()}
            <WebsiteBuilderFooter
                className="presaveContentFooter"
                contentIndex={contentIndex}
                styleOptions={
                    <OptionsSymphony
                        className="presaveStyleOptions"
                        style={{ flexWrap: 'wrap' }}
                        options={PRESAVE_STYLE_OPTIONS}
                        value={presaveContent.style || ''}
                        handleOptionSelected={(option) => {
                            const updatedContent = [...websiteContent];
                            updatedContent[contentIndex].presaveContent.style = option as PresaveWebsiteBuilderStyle;
                            setWebsiteContent(updatedContent);
                        }}
                        size="small"
                        isRadio={false}
                    />
                }
            />
        </CardSymphony>
    )
}

const sty: Record<string, CSSProperties> = {
    presaveContentComponent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    presaveReleaseDetailsCardRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    presaveReleaseDateInfoRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    presaveMusicPlatformRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        padding: 16,
        borderBottom: Sty.border
    },
    presaveMusicPlatformsContent: {
        borderTop: Sty.border,
    }
}

export default PresaveContent;